import { Draggable } from "@hello-pangea/dnd";
import { PackingSlipModel } from "../../../domain/models/packingSlipModel";
import DealerCard from "../DealerCard/DealerCard";
import { useTranslation } from "react-i18next";
import { Form, InputGroup } from "react-bootstrap";
import { RouteDraggableRequest } from "./RouteDraggableRequest";
import ReactDOM from "react-dom";
import { useAppDispatch } from "../../../../../../hooks/useTypedSelector";
import { updatePlanningPackingSlipPickupAmount } from "../../../infrastructure/redux/thunks/packingSlip/updatePlanningPackingSlipPickupAmount";
import { debounce } from "lodash";
import { useCallback } from "react";
import { changePickupAmount } from "../../../infrastructure/redux/PlanningSlice";
import { getRequestsFromDay } from "../../../infrastructure/redux/PlanningThunks";

export const RouteDraggableDealer = ({
  packingSlip,
  index,
  draggableId,
  portal,
}: {
  packingSlip: PackingSlipModel;
  index: number;
  draggableId: string | null;
  portal: HTMLDivElement;
}) => {
  const { t } = useTranslation("common");

  const dispatch = useAppDispatch();

  const debouncedUpdatePickupAmount = useCallback(
    debounce((newValue: string) => {
      void dispatch(
        updatePlanningPackingSlipPickupAmount({
          value: newValue,
          packingSlip,
        }),
      ).then(void dispatch(getRequestsFromDay()));
    }, 1000),
    [],
  );

  function updatePickupAmount(newValue: string | undefined) {
    if (newValue == undefined) return;

    dispatch(changePickupAmount({ newAmount: newValue, packingSlip }));
    debouncedUpdatePickupAmount(newValue);
    return newValue;
  }

  return (
    <Draggable
      key={"slip:" + packingSlip.id}
      draggableId={"slip:" + packingSlip.id}
      index={index}
      isDragDisabled={packingSlip.id.startsWith("state:")}
    >
      {(provided, snapshot) => {
        const child = (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <DealerCard
              key={"dealer-" + packingSlip.id}
              className={"mt-2"}
              open={
                draggableId && !draggableId.startsWith("request")
                  ? false
                  : undefined
              }
            >
              <DealerCard.Header>
                <div className={"d-flex flex-column"}>
                  <div className={"d-flex"}>
                    <h5>{packingSlip.relation.name}</h5>
                  </div>

                  <span>
                    {
                      packingSlip.requests.filter(
                        (request) => request.type == "deliver",
                      ).length
                    }{" "}
                    {t("deliver")} | {packingSlip.pickup_amount ?? 0}{" "}
                    {t("pickup")}
                  </span>
                </div>
              </DealerCard.Header>
              <DealerCard.Content>
                <p>{t("Deliver")}</p>

                <div className={"d-flex flex-wrap"}>
                  {packingSlip.requests
                    .filter((request) => request.type == "deliver")
                    .map((request, index) => {
                      return (
                        <RouteDraggableRequest
                          key={"request:" + request.id}
                          request={request}
                          index={index}
                          portal={portal}
                        />
                      );
                    })}
                </div>

                <p className={"mt-3"}>{t("Pickup")}</p>
                <Form.Group>
                  <InputGroup>
                    <Form.Control
                      className={"bg-white"}
                      id={"pickup_amount"}
                      name={"pickup_amount"}
                      value={packingSlip.pickup_amount ?? 0}
                      type="number"
                      onChange={(e) => {
                        updatePickupAmount(e.target.value);
                      }}
                    />
                  </InputGroup>
                </Form.Group>
              </DealerCard.Content>
            </DealerCard>
          </div>
        );

        if (!snapshot.isDragging) {
          return child;
        }

        return ReactDOM.createPortal(child, portal);
      }}
    </Draggable>
  );
};
